import React from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { getWithSelector } from '@lumapps/lumx/core/utils/selectors';
import {
    SelectionChipGroup as LumXSelectionChipGroup,
    type ChipProps,
    type Selector,
    type Theme,
} from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { LUMX_INPUTS } from '../../keys';

const CLASSNAME = 'lumx-selection-chip-group';

export interface SelectionChipGroupProps<O> {
    /**
     * Option object id selector (either the property name or a function to get the id)
     */
    getOptionId: Selector<O>;
    /**
     * Option object name selector (either the property name or a function to get the name)
     * Fallbacks on the ID if not defined
     */
    getOptionName?: Selector<O, string | undefined | null>;
    /**
     * Selected options array
     */
    value?: O[];
    /**
     * Callback on option array selected
     */
    onChange?(newValue?: O[]): void;
    /**
     * Input ref to restore focus
     */
    inputRef?: React.RefObject<HTMLInputElement>;
    /**
     * Input label, used to generate the chip group aria description
     */
    inputLabel?: string;
    /**
     * Customize how chips should render
     */
    renderChip?: (option: O) => React.ReactNode;
    /**
     * Scope for tracking purposes
     */
    scope?: string;
    /**
     * LumX theme
     */
    theme?: Theme;
    /**
     * Disabled state
     */
    isDisabled?: boolean;
}

/**
 * Selection chip group listing currently selected options in a chip list.
 *
 * Thin wrapper around the LumX SelectionChipGroup that:
 * - Generates the accessibility label from the inputLabel prop
 * - Generates data attributes from the scope prop
 */
export const SelectionChipGroup = <O,>({ inputLabel, scope = CLASSNAME, ...props }: SelectionChipGroupProps<O>) => {
    const { translateKey, translateAndReplace } = useTranslate();
    const { get } = useDataAttributes(scope);

    const label = translateAndReplace(LUMX_INPUTS.SELECTED_VALUES_LIST_LABEL, {
        SELECT_LABEL: inputLabel || translateKey(GLOBAL.VALUES),
    });

    const chipRemoveLabel = translateKey(GLOBAL.REMOVE);

    const { getOptionId } = props;

    const getChipProps = React.useCallback(
        (option: O) => get({ element: 'chip', action: getWithSelector(getOptionId, option) }) as Partial<ChipProps>,
        [get, getOptionId],
    );

    return (
        <LumXSelectionChipGroup
            {...props}
            label={label}
            chipRemoveLabel={chipRemoveLabel}
            getChipProps={getChipProps}
        />
    );
};
